import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Search, ShieldCheck, Store, Trash2, UserPlus, History } from "lucide-react";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", role: "associate" });
  const [inviteResult, setInviteResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => { const { data } = await api.get("/users", { params: q ? { q } : {} }); setItems(data); };
  const loadLogs = async () => { try { const { data } = await api.get("/role-history"); setLogs(data); } catch {} };
  useEffect(() => { load(); loadLogs(); /* eslint-disable-next-line */ }, [q]);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try { await api.put(`/users/${u.id}/role`, { role }); toast.success("Rol actualitzat"); load(); loadLogs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}/active`, { active: !u.active }); toast.success(!u.active ? "Compte activat" : "Compte desactivat"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Eliminar el compte de ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Compte eliminat"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const doInvite = async () => {
    if (!invite.email.includes("@")) return toast.error("Email vàlid obligatori");
    try {
      const { data } = await api.post("/users/invite", { ...invite, origin_url: window.location.origin });
      setInviteResult(data); toast.success("Invitació enviada per email"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-accent">Rols i accessos</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Usuaris</h1>
          <p className="text-sm text-muted-foreground mt-1">Un <strong>admin</strong> accedeix al backoffice. Un <strong>comerç</strong> a la seva zona privada.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cerca email o nom..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full w-64" data-testid="users-search-input" />
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => setHistoryOpen(true)} data-testid="users-history-btn"><History className="w-4 h-4 mr-2" /> Historial rols</Button>
          <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) { setInvite({ email: "", name: "", role: "associate" }); setInviteResult(null); } }}>
            <DialogTrigger asChild><Button className="rounded-full" data-testid="users-invite-btn"><UserPlus className="w-4 h-4 mr-2" /> Convidar</Button></DialogTrigger>
            <DialogContent data-testid="invite-dialog">
              <DialogHeader><DialogTitle>Convidar nou usuari</DialogTitle></DialogHeader>
              {!inviteResult ? (
                <div className="space-y-3">
                  <div><Label>Email</Label><Input type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" /></div>
                  <div><Label>Nom (opcional)</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name" /></div>
                  <div><Label>Rol</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="associate">Comerç associat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">L'usuari rebrà un email amb un enllaç d'un sol ús per posar la seva contrasenya.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="invite-result">
                  <div className={`rounded-xl p-4 border ${inviteResult.email_sent ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" : "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800"}`}>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {inviteResult.email_sent ? "✅ Email enviat correctament" : "⚠️ No hem pogut enviar l'email"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {inviteResult.email_sent
                        ? "Si no arriba en 5 min, revisa la carpeta d'spam/promocions."
                        : "El proveïdor d'email ha rebutjat el missatge. Comparteix manualment l'enllaç d'sota amb el destinatari."}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Enllaç d'invitació d'un sol ús</Label>
                    <div className="mt-1 flex gap-2">
                      <Input readOnly value={inviteResult.accept_url} className="rounded-xl font-mono text-xs" data-testid="invite-accept-url" />
                      <Button type="button" onClick={() => { navigator.clipboard.writeText(inviteResult.accept_url); toast.success("Enllaç copiat"); }} variant="outline" className="rounded-full shrink-0" data-testid="invite-copy-btn">Copiar</Button>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                {!inviteResult ? <Button onClick={doInvite} data-testid="invite-send-btn">Enviar invitació</Button> : <Button onClick={() => setInviteOpen(false)}>Tancar</Button>}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-2xl border-2 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Actiu</TableHead><TableHead>Alta</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {u.role === "admin" ? <div className="w-8 h-8 rounded-lg bg-accent/15 text-accent flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div> : <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Store className="w-4 h-4" /></div>}
                      <div>
                        <div className="font-medium text-sm">{u.name || "—"}</div>
                        {u.id === me?.id && <div className="text-[10px] uppercase tracking-widest text-accent">Tu</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm font-mono">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="rounded-full w-36 h-9" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="associate">Comerç</SelectItem></SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.id}`} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>{u.id !== me?.id && <Button size="icon" variant="ghost" onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sense usuaris.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader><DialogTitle>Historial de canvis de rol</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {logs.length === 0 && <div className="text-muted-foreground text-center py-6">Encara sense canvis registrats.</div>}
            {logs.map((l) => (
              <div key={l.id} className="p-3 rounded-xl bg-secondary/60 text-xs" data-testid={`history-log-${l.id}`}>
                <div><strong className="text-primary">{l.actor_email}</strong> ha canviat el rol de <strong>{l.target_email}</strong></div>
                <div className="mt-1 text-muted-foreground">{l.old_role} → <strong className="text-foreground">{l.new_role}</strong> · {new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
